// src/components/users/UserCard.jsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { Mail, Phone, Book, Calendar, MoreVertical, User, Trash2 } from 'lucide-react';

export const UserCard = ({ user, onUpdate }) => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const { put, del } = useApi();
  const [showActions, setShowActions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleStatusChange = async (newStatus) => {
    try {
      setLoading(true);
      await put(`/users/${user.id}/status`, { isActive: newStatus });
      onUpdate();
    } catch (error) {
      console.error('Failed to update user status:', error);
    } finally {
      setLoading(false);
      setShowActions(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      setLoading(true);
      await del(`/users/${user.id}`);
      onUpdate();
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
      setShowActions(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      student: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      counselor: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      receptionist: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
    };
    return colors[role] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow border border-transparent dark:border-gray-700 transition-colors">
      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {user.fullName || user.username}
            </h3>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
              {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
            </span>
          </div>
          {['admin', 'receptionist', 'hod'].includes(currentUser.role) && (
            <div className="relative">
              <button
                onClick={() => setShowActions(!showActions)}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              >
                <MoreVertical className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>

              {showActions && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-md shadow-lg py-1 z-10 border border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => navigate(`/users/${user.id}`)}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    View Profile
                  </button>
                  <button
                    onClick={() => navigate(`/users/${user.id}/edit`)}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    Edit User
                  </button>
                  <button
                    onClick={() => handleStatusChange(!user.isActive)}
                    className={`block w-full text-left px-4 py-2 text-sm ${user.isActive ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                      } hover:bg-gray-100 dark:hover:bg-slate-700`}
                    disabled={loading}
                  >
                    {user.isActive ? 'Deactivate User' : 'Activate User'}
                  </button>
                  {currentUser.role === 'admin' && (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      disabled={loading}
                    >
                      <div className="flex items-center">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete User
                      </div>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Details */}
        <div className="space-y-2">
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
            <Mail className="h-4 w-4 mr-2" />
            {user.email}
          </div>
          {user.phone && (
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <Phone className="h-4 w-4 mr-2" />
              {user.profile.phone}
            </div>
          )}
          {user.role === 'student' && (
            <>
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <Book className="h-4 w-4 mr-2" />
                {user.profile?.major || 'Major not specified'}
              </div>
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <Calendar className="h-4 w-4 mr-2" />
                Year {user.profile?.yearOfStudy}
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-4 flex space-x-3">
          <button
            onClick={() => navigate(`/users/${user.id}`)}
            className="flex-1 px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
          >
            View Profile
          </button>
          {currentUser.role === 'admin' && user.role === 'student' && (
            <button
              onClick={() => navigate(`/users/${user.id}/sessions`)}
              className="flex-1 px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-md transition-colors"
            >
              View Sessions
            </button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Deletion</h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete <strong>{user.fullName || user.username}</strong>?
              This action cannot be undone and will permanently remove all user data.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-300"
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
